import { addDays, addMonths, addWeeks, format, isValid, parseISO } from 'date-fns';
import type { DataPoint } from '@/features/chartsView';
import { CHART_BASE_DIMENSION } from './constants';

const WEEK_PATTERN = /^(\d{4})-W(\d{1,2})(?=\s|$)/;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}(?=[\sT]|$)/;
const MONTH_PATTERN = /^\d{4}-\d{2}(?=\s|$)/;

const ISO_DATE_LENGTH = 10;

const resolveNextBucketStart = (label: string): Date | null => {
  const week = WEEK_PATTERN.exec(label);

  if (week) {
    const [, year, weekNumber] = week;
    const start = parseISO(`${year}-W${weekNumber.padStart(2, '0')}`);

    return isValid(start) ? addWeeks(start, 1) : null;
  }

  // Must run before the month check — "2026-10-14" would otherwise parse as October 2026.
  const day = DAY_PATTERN.exec(label);

  if (day) {
    const start = parseISO(day[0]);

    return isValid(start) ? addDays(start, 1) : null;
  }

  const month = MONTH_PATTERN.exec(label);

  if (month) {
    const start = parseISO(month[0]);

    return isValid(start) ? addMonths(start, 1) : null;
  }

  return null;
};

export const resolveCohortCutoffs = (
  labels: string[],
): Map<string, string> => {
  const cutoffs = new Map<string, string>();

  for (const label of labels) {
    const nextStart = resolveNextBucketStart(label);

    if (nextStart) {
      cutoffs.set(label, format(nextStart, 'yyyy-MM-dd'));
    }
  }

  return cutoffs;
};

interface SelectedCohortCutoffParams {
  enabled: boolean;
  splitDimension: string;
  isChronological: boolean;
  splitOptions: string[] | undefined;
  excludedSplitOptions: string[];
}

export const resolveSelectedCohortCutoffs = ({
  enabled,
  splitDimension,
  isChronological,
  splitOptions,
  excludedSplitOptions,
}: SelectedCohortCutoffParams): Map<string, string> => {
  if (!enabled) {
    return new Map();
  }

  if (splitDimension === CHART_BASE_DIMENSION || !isChronological) {
    return new Map();
  }

  const excluded = new Set(excludedSplitOptions);

  return resolveCohortCutoffs(
    (splitOptions ?? []).filter((option) => !excluded.has(option)),
  );
};

export const trimImmatureCohorts = <T extends string>(
  data: DataPoint<T>[],
  xKey: T,
  cutoffs: Map<string, string>,
): DataPoint<T>[] => {
  if (cutoffs.size === 0) {
    return data;
  }

  const trimmed: DataPoint<T>[] = [];

  for (const point of data) {
    const snapshot = point[xKey].slice(0, ISO_DATE_LENGTH);
    const mature: Record<string, Record<string, number>> = {};
    let hasMature = false;

    for (const [option, values] of Object.entries(point.data)) {
      const cutoff = cutoffs.get(option);

      if (cutoff === undefined || snapshot >= cutoff) {
        mature[option] = values;
        hasMature = true;
      }
    }

    if (hasMature) {
      trimmed.push({ ...point, data: mature });
    }
  }

  return trimmed;
};

const hasVisibleValues = <T extends string>(
  point: DataPoint<T>,
  excluded: Set<string>,
): boolean => Object.keys(point.data).some((option) => !excluded.has(option));

export const hasVisibleCohortData = <T extends string>(
  data: DataPoint<T>[],
  excludedSplitOptions: string[],
): boolean => {
  const excluded = new Set(excludedSplitOptions);

  return data.some((point) => hasVisibleValues(point, excluded));
};

export const narrowToVisibleSnapshots = <T extends string>(
  data: DataPoint<T>[],
  excludedSplitOptions: string[],
): DataPoint<T>[] => {
  const excluded = new Set(excludedSplitOptions);

  let first = 0;

  while (first < data.length && !hasVisibleValues(data[first], excluded)) {
    first += 1;
  }

  // Emptying here would read as `noData`, which wins over `noSelection` in the wrapper.
  if (first === data.length) {
    return data;
  }

  let last = data.length - 1;

  while (last > first && !hasVisibleValues(data[last], excluded)) {
    last -= 1;
  }

  if (first === 0 && last === data.length - 1) {
    return data;
  }

  return data.slice(first, last + 1);
};
